import { Head, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { ChangeLogSubNav } from "@/components/admin/ChangeLogSubNav"
import { Badge } from "@/components/ui/badge"
import { Select } from "@/components/ui/select"
import type { RetentionRow } from "@/types/change_log"

type Props = {
  logs: RetentionRow[]
  choices: { value: number | null; label: string }[]
}

const DESCRIPTION = "How long each change log's entries are kept."

export default function ChangeLogRetention({ logs, choices }: Props) {
  const setKeepFor = (log: RetentionRow, value: string) => {
    router.patch(
      "/admin/change_log/retention",
      { module: log.module, keep_for_days: value },
      { preserveScroll: true },
    )
  }

  return (
    <>
      <Head title="Change log retention">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Change log retention" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <PageHeader
          title="Change log"
          description="How long each log's entries are kept."
          tabs={<ChangeLogSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <div className="callout callout-accent mb-6">
            <p className="m-0">
              <strong>Nothing is deleted today.</strong> Every log is set to keep entries
              for ever, and there is no job that removes them. Choosing a period here
              records the choice; a later release will act on it.
            </p>
          </div>

          <ul className="m-0 list-none divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page p-0">
            {logs.map((log) => (
              <li key={log.module} className="flex flex-wrap items-start gap-4 px-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Badge tone={log.tone}>{log.label}</Badge>
                  </div>
                  <p className="m-0 mt-1.5 text-sm text-ink-body">{log.description}</p>
                  <p className="m-0 mt-1 text-xs text-ink-muted">
                    {log.entry_count.toLocaleString()}{" "}
                    {log.entry_count === 1 ? "entry" : "entries"} recorded ·{" "}
                    {log.changed_by
                      ? `Set by ${log.changed_by} ${log.changed_at_label}`
                      : "Never changed"}
                  </p>
                </div>

                <div className="w-44 shrink-0">
                  <label htmlFor={`keep_${log.module}`} className="sr-only">
                    Keep {log.label} entries for
                  </label>
                  <Select
                    id={`keep_${log.module}`}
                    value={log.keep_for_days === null ? "" : String(log.keep_for_days)}
                    onChange={(event) => setKeepFor(log, event.target.value)}
                  >
                    {choices.map((choice) => (
                      <option
                        key={choice.label}
                        value={choice.value === null ? "" : String(choice.value)}
                      >
                        {choice.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </AdminShell>
    </>
  )
}
